// Frame types — the frame's state product, its overlay/notice unions, and the
// FrameTransition sum (every named arrow, one variant per intent).
//
// Split out of `app/frame.ts` (C1 hotspot decomposition). Type declarations
// only, plus the resting-overlay constant. The pure fold that consumes these
// lives in the sibling `frame/fold.ts`; the entry `../frame.ts` keeps
// `initialFrame` + re-exports this whole surface at the stable path.

import type {
    NavRailState,
    OpenSecondary,
    SecondarySlot,
} from "../../components/AppShell/AppShell.js";
import type { ChatAnchor } from "../../components/Chat/presentation.js";
import type { FocusHistory } from "../../components/Navigation/focusHistory.js";
import type { PaneId, PaneTree } from "../../components/Navigation/paneTree.js";
import type {
    ActiveTab,
    ClosedTabs,
    ConversationId,
    FocusTarget,
    MaterialRef,
    ResourceId,
    ResourceRef,
    ResourceTab,
    SourceRef,
} from "../../components/Navigation/types.js";
import type { SearchFacet } from "../../components/Search/searchModel.js";
import type { SettingsSectionKind } from "../../components/Settings/types.js";
import type { DeskState } from "../../components/Studio/studioDesk.js";
import type { TurnFailure } from "../../modules/chat/model.js";
import type { MessageQueue, QueueControl } from "../messageQueue.js";

// === Overlay — the one transient surface floated over the frame ==============
// At most one at a time; each intent is its own named variant and `none` is an
// explicit value, so "no overlay" is in the sum rather than a null to check.
export type NoOverlay = { readonly type: "none" };
// The ONE command surface — search + run-a-command, opened by the omnibar, the
// ⌘K chord, and the rail's per-kind "Show all" doors. Rendered as a dropdown
// the TopStrip anchors under the omnibar (not a centred modal).
export type CommandOverlay = { readonly type: "command" };
export type CreateOverlay = { readonly type: "create" }; // + New material (creation dialog)
export type InsertOverlay = { readonly type: "insert" }; // editor block palette (insert)
// The export sheet, for one material: the edited one (its toolbar, the
// omnibar) or any material card in the Studio.
export type ExportOverlay = {
    readonly type: "export";
    readonly materialId: string;
};
export type HistoryOverlay = { readonly type: "history" }; // History & Inspection surface
// The fuzzy content-finder (.claude/analysis/fuzzy-finder.md): a heavier, centred
// two-pane modal (results + preview) summoned by ⌘⇧F. Joins `OverlaySurface`
// automatically, so the existing `open-overlay` intent summons it (no reducer
// change) — modelled on HistoryOverlay, NOT the omnibar's dropdown.
export type FinderOverlay = { readonly type: "finder" };
// The Diátaxis help overlay: the four-quadrant documentation surface
// (Tutorials / How-to / Reference / Explanation), promoted from
// sketches/help. Joins OverlaySurface automatically, summoned by open-help.
// Reachable from the omnibar's "Help" command and the guided tour's "What is
// this?" link — NOT from the meta corner "?" anymore (dogfood F13: that
// button's own "Help & shortcuts" menu was outdated and is gone).
export type HelpOverlay = { readonly type: "help" };
// The workspace Search + Recent surface was RETIRED as an overlay (dogfood F6):
// it is now a full-page singleton CENTRE tab (`open-search`, ActiveSearch), so a
// roomy master-detail browse finally has the room a 240px rail / a modal could
// not give it. See `SearchCentre` + the `search` arm in Centre.tsx.
export type Overlay =
    | NoOverlay
    | CommandOverlay
    | CreateOverlay
    | InsertOverlay
    | ExportOverlay
    | HistoryOverlay
    | FinderOverlay
    | HelpOverlay;
// What `open-overlay` may summon — everything except the resting state.
export type OverlaySurface = Exclude<Overlay, NoOverlay>;

export const noOverlay: NoOverlay = { type: "none" };

// === Chat — the Assistant lives ONLY as its conversation TAB =================
// The retired corner float (a bottom-right overlay resting as a minimized handle
// or an unfolded compact box) is GONE. The Assistant has ONE home: its own
// conversation tab in a pane, opened/activated by `enter-full-chat`. Whether the
// conversation "owns the centre" is therefore NOT a stored frame factor — it is
// exactly `tabs.active.type === "conversation"`, derived from the working set. So
// there is no `chat` presentation state to fold or land into: navigating away
// from the conversation is just activating another tab; closing it is just
// closing a tab. (chat-composer-states.md's float model is retired.)

// === Notice — the transient outcome the host surfaces as a toast =============
// A named union, one variant per intent (distinct variants, never a flag).
// The failure variants carry their localized message because the copy comes
// from `toDisplay`'s vocabulary, not a static record. "resource-deleted" is
// the UNDO notice (master audit R5): it carries exactly what its undo needs.
// A save that can be run again from its error message (one per intent).
export type SaveRetry =
    | {
          readonly type: "rename-conversation";
          readonly id: string;
          readonly title: string;
      }
    | { readonly type: "archive-conversation"; readonly id: string }
    | { readonly type: "unarchive-conversation"; readonly id: string };

export type HostNotice =
    | { readonly type: "attach" }
    | { readonly type: "reference" }
    | { readonly type: "media" }
    | { readonly type: "edit-failed" }
    | { readonly type: "turn-failed"; readonly failure: TurnFailure }
    | {
          readonly type: "ingest-failed";
          readonly filename: string;
          readonly message: string;
      }
    // A byte-identical re-upload silently aliased to an existing Source
    // (DB4 transparent reuse) — no new source, no re-analysis. A brief,
    // non-blocking notice, never a blocking dialog: the upload already
    // resolved to the existing resource by the time this shows.
    // `existingTitle` names it when resolvable off the loaded library index
    // (the wire's own `AddedSource.title` on this outcome is unreliable —
    // see the `refFor` lookup at the call site); `undefined` degrades to an
    // honest name-free message rather than fabricating one.
    // `onCanvas`: the Studio is showing and its card was highlighted there
    // (the same item is never on the canvas twice); the note is shown at the
    // card (`sourceId`).
    | {
          readonly type: "source-deduped";
          readonly sourceId: string;
          readonly existingTitle: string | undefined;
          readonly onCanvas: boolean;
      }
    | {
          readonly type: "resource-deleted";
          readonly ref: ResourceRef;
          readonly priorContentHash: string;
      }
    // The conversation sibling of `resource-deleted`. A conversation carries
    // NO contentHash (its log is append-only; there is no freshness gate — see
    // ports/conversation.ts), so this variant keys the Undo on the id + title
    // alone. An honest divergence from the resource notice, not a fake hash.
    | {
          readonly type: "conversation-deleted";
          readonly conversationId: string;
          readonly title: string;
      }
    // Cards went into a new module (the Studio's container tool). Its Undo
    // deletes the module; the cards stay as they were.
    | {
          readonly type: "module-made";
          readonly ref: ResourceRef;
          readonly count: number;
      }
    // Cards went into a new course (a module dropped on a module, the card
    // menu, the container tool). Its Undo deletes the course; the modules
    // stay.
    | {
          readonly type: "course-made";
          readonly ref: ResourceRef;
          readonly count: number;
      }
    // Cards were added to a module or course as its parts; `number` is the first one's
    // (0: unknown). Its Undo takes them back out.
    | {
          readonly type: "parts-added";
          readonly moduleId: string;
          readonly parts: readonly string[];
          readonly number: number;
      }
    // A part moved in its container's reading order (`from`, `to`: 0-based).
    // Its Undo moves it back.
    | {
          readonly type: "part-moved";
          readonly moduleId: string;
          readonly part: string;
          readonly from: number;
          readonly to: number;
      }
    // A part was taken out of its container (it stays on the canvas); `at`
    // was its place. Its Undo puts it back there.
    | {
          readonly type: "part-removed";
          readonly moduleId: string;
          readonly part: string;
          readonly at: number;
          readonly kind: "module" | "course";
      }
    // A card dropped on a module or course it is already part `number` of
    // (1-based): nothing changes; the part is shown at the object.
    | {
          readonly type: "already-part";
          readonly moduleId: string;
          readonly part: string;
          readonly number: number;
      }
    // Cards taken off the Studio canvas (never deleted); Undo puts them back.
    // `title` names the first one.
    | {
          readonly type: "removed-from-canvas";
          readonly ids: readonly string[];
          readonly title: string;
      }
    // A focus on the Studio desk closed (put back, or a click outside it);
    // `previous` is what was open (view state), which its Undo reopens.
    | {
          readonly type: "desk-put-back";
          readonly previous: DeskState;
      }
    | { readonly type: "lifecycle-failed"; readonly message: string }
    // A project set aside (its "…" menu, or "Archive instead"); its Undo
    // restores it.
    | {
          readonly type: "project-archived";
          readonly projectId: string;
          readonly title: string;
      }
    // A conversation set aside (its "…" menu, or "Archive instead" in the
    // delete dialog); its Undo brings it back.
    | {
          readonly type: "conversation-archived";
          readonly conversationId: string;
          readonly title: string;
      }
    // A save the author asked for failed: the row is back as it was, and
    // Retry runs the same save again.
    | {
          readonly type: "save-failed";
          readonly retry: SaveRetry;
          readonly message: string;
      }
    // A copy the author asked for landed on the clipboard; `label` names
    // what ("version 0.5.0 (a1b2c3d)"), already localized by the shell.
    | { readonly type: "copied"; readonly label: string }
    // The browser refused the clipboard write.
    | { readonly type: "copy-failed" }
    // A source's missing content was restored by re-uploading its original
    // file (same source, references intact). `conversationId` present ⇒ the
    // re-upload came from that chat, whose turn stopped on the gap — the
    // notice offers "Continue" there.
    | {
          readonly type: "source-restored";
          readonly sourceTitle: string | undefined;
          readonly conversationId: string | undefined;
      }
    // A download succeeded — the transient "Downloaded X" confirmation that
    // closes the silent-download hole (feedback sketch F1). Carries the saved
    // file's name for the toast copy.
    | { readonly type: "exported"; readonly filename: string }
    // Dev-command feedback (DX doc §5) — three DISTINCT variants, never a flag
    // on one. Every dev command acknowledges within 100ms (`dev-started`) and
    // reports its outcome with detail + timing (`dev-succeeded` carries the
    // measured duration; `dev-failed` carries the server error detail). Only
    // ever dispatched under `import.meta.env.DEV`; `label` is the plain-English
    // phrase for the command (dev-only, so not localized).
    | { readonly type: "dev-started"; readonly label: string }
    | {
          readonly type: "dev-succeeded";
          readonly label: string;
          readonly durationMs: number;
      }
    | {
          readonly type: "dev-failed";
          readonly label: string;
          readonly message: string;
      }
    // Aggregate-scoped replay degradation surfaced at boot (determinism M7
    // phase 2, ADR-3). The CALM user-facing half: counts only, no event
    // detail (that lives in the server dev log + inspector). `frozen` =
    // aggregates frozen at their last-good event; `unroutable` = unknown-
    // type events that couldn't be attributed. Both preserved, never
    // dropped — the copy reassures nothing was lost.
    | {
          readonly type: "replay-degraded";
          readonly frozen: number;
          readonly unroutable: number;
      };

// === Panel focus — the right panel's focus model (openRef cluster) ===========
// The right panel is a WINDOW that either shows the conversation OVERVIEW or
// FOCUSES one entity (IDD 4ba6bbc0). Modelled as a discriminated union, NOT a
// nullable "focusedId": overview is a first-class state (the roster dashboard),
// not "no entity selected". When an entity is focused it may carry a transient
// FocusTarget (WHICH span to reveal) that the panel resolves at the render edge
// (Phase 2/3 of the openRef cluster); a bare focus needs no target. Focus is
// frame state (survives nothing loaded; carries the panel's meaning), written
// ONLY by the FocusOverview / FocusEntity transitions below.
type PanelOverview = { readonly type: "overview" };
// How the panel reached a focused SOURCE from one claim in a material (a
// citation in the grounding lens, a gutter anchor): the source view then shows
// that claim + its passage on top, and its one step back returns to the claim.
export type StatementOrigin = {
    readonly type: "statement";
    readonly material: MaterialRef;
    readonly statementId: string;
};
// `part` = the part of the focused material to open at (a course's module,
// bd-lya): opening a part focuses its course and opens the part there.
type PanelEntity = {
    readonly type: "entity";
    readonly ref: ResourceRef;
    readonly target?: FocusTarget;
    readonly origin?: StatementOrigin;
    readonly part?: MaterialRef;
};
type PanelFocus = PanelOverview | PanelEntity;

// The resting focus — the conversation overview. Exported so `initialFrame`
// seeds from the ONE value (mirrors `noOverlay`).
export const panelOverview: PanelFocus = { type: "overview" };

// === FrameState — the product of every frame factor ==========================
// One value per region/factor; nothing here is loaded data (library, elements,
// conversation live outside — they are what the frame SHOWS, not the frame).
export type FrameState = {
    // The rail's display state.
    readonly nav: NavRailState;
    // The one transient surface, or `none`.
    readonly overlay: Overlay;
    // Which secondary slot is summoned, or null for Focus (closed).
    readonly openSecondary: OpenSecondary;
    // The right panel's FOCUS — the conversation overview, or one focused
    // entity (+ an optional span target). See PanelFocus above.
    readonly panelFocus: PanelFocus;
    // What the chat's resource scope follows.
    readonly anchor: ChatAnchor;
    // Which section the Settings tab shows (account / data / appearance / …).
    // Frame state, not tab state — `open-settings {section}` sets it and the
    // Settings tab reads it; a re-open to a specific section (e.g. "where your
    // data lives" → Data) just updates this and focuses the one tab.
    readonly settingsSection: SettingsSectionKind;
    // The project the author is in: the Studio shows it (`/projects/:id`).
    // Frame state like `settingsSection`, so leaving the Studio (the
    // Library, the Knowledge bank) and coming back returns to the same
    // project; `open-studio {projectId}` sets it. null: none chosen yet.
    readonly studioProject: string | null;
    // The workspace's ONE remembered pane tree (the author's working memory —
    // a single-leaf tree pre-split, holding the tab strip as its leaf).
    readonly paneTree: PaneTree;
    // The workspace's recently-closed resources (the reopen stack).
    readonly closedTabs: ClosedTabs;
    // The workspace's session recency (newest first, capped).
    readonly recents: readonly ResourceRef[];
    // The window's ONE global focus-history stack — the back/forward cursor
    // over focused VIEWS. Session-only: seeded fresh, never read from
    // `viewState`, thrown away on reload by design (focusHistory.ts header).
    // The blanket recorder in `foldFrame` keeps `history.current` equal to the
    // actually-focused view, so `nav-back`/`nav-forward` re-materialize the
    // right view (D1).
    readonly history: FocusHistory;
    // The blocked-close confirmation (closeTab reported `unsaved`), or null.
    readonly confirmClose: ResourceTab | null;
    // The messages showing: toasts, the last-step line, the note at an object
    // (messageQueue.ts).
    readonly messages: MessageQueue<HostNotice>;
};

// === FrameTransition — every arrow, one named variant per intent =============

// --- Rail (regions: nav) --
export type CollapseRail = { readonly type: "collapse-rail" };
export type ExpandRail = { readonly type: "expand-rail" };
export type HideRail = { readonly type: "hide-rail" };
// expanded ↔ collapsed; a hidden rail (narrow web fallback) expands.
export type ToggleRail = { readonly type: "toggle-rail" };

// --- Overlay (regions: overlay) --
export type OpenOverlay = {
    readonly type: "open-overlay";
    readonly overlay: OverlaySurface;
};
export type CloseOverlay = { readonly type: "close-overlay" };

// --- Right panel (regions: openSecondary; + paneTree when the conversation tab
// was active — summoning parks it on its neighbour so the pane and the centre
// never show the same surface twice) --
// Summon one slot — the ONE in-world path the toolbar group, the contextual
// jumps and the ⌥1/⌥2/⌥3 accelerators share. Any active conversation tab is
// parked on its neighbour so the pane and the centre never show it twice.
export type SummonSlot = {
    readonly type: "summon-slot";
    readonly slot: SecondarySlot;
};
// Close the pane (regions: openSecondary).
export type CloseSecondary = { readonly type: "close-secondary" };
// The right dock's two panels, each its OWN named intent (distinct-commands,
// never one summon-panel{slot} flag) — the ribbon, chords, and omnibar are
// projections of these. TOGGLE semantics: open the dock body to the panel, or
// collapse it if that panel is already open (regions: openSecondary).
export type SummonReview = { readonly type: "summon-review" };
export type SummonQuality = { readonly type: "summon-quality" };
// Toggle the chat's panel (right panel v2) — its own named intent, the
// Review/Quality sibling (never one summon-panel{slot} flag).
type SummonPanel = { readonly type: "summon-panel" };

// --- Panel focus (regions: panelFocus) --
// Zoom the right panel OUT to the conversation overview (the breadcrumb's
// "Översikt" segment). Its OWN intent, never a flag on FocusEntity — one
// command per intent. Unexported (dispatched as a literal), mirroring the
// SummonFiles / OpenHome siblings.
type FocusOverview = { readonly type: "focus-overview" };
// Focus the right panel on ONE entity — a teleport from the panel header (and,
// from Phase 3, an openRef consumer). `target` optionally names the span to
// reveal within it (resolved at the render edge). A DISTINCT command from
// FocusOverview; the ref carries the entity's identity + kind + title.
type FocusEntity = {
    readonly type: "focus-entity";
    readonly ref: ResourceRef;
    readonly target?: FocusTarget;
};

// SHOW one entity in the right panel: summon the focus host (the chat panel)
// AND focus it there — the ONE "open" of every reference in the chat (tool-step
// chips, read locators, agent links, @-mentions, cards, the auto-show after a
// create). `target` optionally names WHERE in it to open (a page a tool read).
// Distinct from FocusEntity, which only re-points an already-open panel
// (regions: openSecondary, panelFocus).
type ShowInPanel = {
    readonly type: "show-in-panel";
    readonly ref: ResourceRef;
    readonly target?: FocusTarget;
};
// Show the source behind ONE claim of a material (right panel v2): the source,
// at the quote's page, remembering the claim for the named step back. Its own
// intent, never a flag on ShowInPanel (regions: openSecondary, panelFocus).
type ShowStatementSource = {
    readonly type: "show-statement-source";
    readonly source: SourceRef;
    readonly origin: StatementOrigin;
    readonly target?: FocusTarget;
};

// --- Chat (regions as noted) --
// The conversation takes the centre as its OWN TAB — a real TabSet member
// (D3): it opens (or re-activates) in the working set, becoming the active tab
// (which is what makes the conversation own the centre). ⌘J (`summon-assistant`)
// routes here through the shell, so the Assistant's one home is this tab
// (regions: openSecondary, paneTree).
export type EnterFullChat = {
    readonly type: "enter-full-chat";
    readonly conversationId: ConversationId;
};
// The source view's "Create material from this source" CTA (P2.3, option A).
// The COMMITMENT to author: it pins the source as the chat's scope anchor and
// opens/activates the FULL conversation TAB (the keystone chat + context room)
// so material can grow live beside the conversation. The conversation id is
// shell-minted (the host ensures the ambient conversation exists before
// dispatching); `source` is the ResourceRef pinned as the scope anchor. The host
// separately introduces the source into the conversation's Context so it also
// shows in the ContextSideCard's Grounding and grounds the turn (that write is
// impure, so it lives in the shell, not this pure fold) (regions: anchor,
// openSecondary, paneTree).
export type CreateMaterialFromSource = {
    readonly type: "create-material-from-source";
    readonly conversationId: ConversationId;
    readonly source: ResourceRef;
};
// Close the conversation's TAB (the strip's close button / the desktop
// close-tab command): the tab leaves the working set and focus falls to its
// neighbour. Closing the tab while it is parked inactive touches only the
// working set (regions: paneTree).
export type CloseConversationTab = {
    readonly type: "close-conversation-tab";
    readonly conversationId: ConversationId;
    // The SHELL-MINTED fresh id used only if this close empties the working set
    // (never-zero-tabs seeds a genuinely new conversation, not a colliding
    // deterministic seed — bug: closing the last tab resurfaced a durable
    // same-named conversation's stale transcript). Ids never in the pure fold
    // (§3.5), so the shell mints it (`mkConversationId`).
    readonly freshConversationId: ConversationId;
};
export type PinChat = {
    readonly type: "pin-chat";
    readonly resource: ResourceRef;
}; // regions: anchor
export type UnpinChat = { readonly type: "unpin-chat" }; // regions: anchor

// --- Tabs (regions as noted) --
// Open a resource into the working set (existing tab activates — never a
// duplicate) and remember it in session recency (regions: paneTree, recents).
export type OpenResource = {
    readonly type: "open-resource";
    readonly ref: ResourceRef;
    // Where in it to open (e.g. a review item's element in a material).
    readonly target?: FocusTarget;
};
// Activate an existing tab — the conversation tab included (the strip's
// click and the ⌘-cycle land here). Activating the conversation makes it own
// the centre; activating anything else moves the active tab away from it
// (regions: paneTree).
export type ActivateTab = {
    readonly type: "activate-tab";
    readonly tab: ActiveTab;
};
// The guarded close: a clean tab closes and is remembered on the reopen
// stack; a dirty one blocks into the confirm dialog (regions: paneTree,
// closedTabs, confirmClose).
export type CloseTab = {
    readonly type: "close-tab";
    readonly id: ResourceId;
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};
// Close whichever tab is ACTIVE in the focused pane — ⌘W and the desktop
// "Close tab" menu item (bridged to the id-less wire command `close-tab`,
// distinct from the `CloseTab` frame command above). Its own intent, not a
// `tabs.active.type` switch bolted onto the shell: the OLD shell switch read
// `TabSet.active`, which presents a kept-active resource and a preview-active
// one IDENTICALLY (`{type: "resource", id}`), so it structurally could not
// route a preview close to the unguarded preview-drop — the confirmed ⌘W
// no-op (tab-model-keyboard-routing-root-cause.md, Fix A). The reducer reads
// the Leaf directly and picks: the preview-drop, the guarded kept-tab close,
// or the matching singleton's own close intent (regions: whichever picked arm
// touches).
export type CloseActiveTab = {
    readonly type: "close-active-tab";
    // Forwarded to whichever per-target close intent this resolves to, for the
    // never-zero-tabs seed (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};
// Batch curation: close every OTHER clean tab / every clean tab to the RIGHT of
// `id`, remembering each on the reopen stack. Dirty tabs are left open (a batch
// never silently discards) (regions: paneTree, closedTabs).
export type CloseOtherTabs = {
    readonly type: "close-other-tabs";
    readonly id: ResourceId;
};
export type CloseTabsToRight = {
    readonly type: "close-tabs-to-right";
    readonly id: ResourceId;
};
// The confirm dialog's outcomes — distinct intents, never a flag.
export type KeepUnsavedTab = { readonly type: "keep-unsaved-tab" }; // regions: confirmClose
export type DiscardUnsavedTab = {
    readonly type: "discard-unsaved-tab";
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
}; // regions: paneTree, closedTabs, confirmClose
// Pop the reopen stack and open the resource fresh (regions: closedTabs +
// open-resource's regions).
export type ReopenClosedTab = { readonly type: "reopen-closed-tab" };
// Force-close a resource's tab without remembering it — the resource is gone
// (post-delete) or unreadable (the error pane's Close tab) (regions: paneTree).
export type DropResourceTab = {
    readonly type: "drop-resource-tab";
    readonly id: ResourceId;
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};
// A rename went through: patch the title everywhere the id appears (regions:
// paneTree, recents).
export type RetitleResource = {
    readonly type: "retitle-resource";
    readonly id: ResourceId;
    readonly title: string;
};
// The workspace's library loaded: re-establish the (possibly restored)
// working set against the truth — stale tabs drop, titles refresh (U46)
// (regions: paneTree).
export type ReconcileTabs = {
    readonly type: "reconcile-tabs";
    readonly refs: readonly ResourceRef[];
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab):
    // a reconcile that drops every surviving resource tab re-seeds a fresh
    // conversation.
    readonly freshConversationId: ConversationId;
};

// --- Settings (regions as noted) --
// Open the Settings singleton TAB and set its section — the working set's other
// tabs (the conversation included) wait exactly where they were. Re-entering
// while already in Settings only re-sections (regions: settingsSection,
// paneTree).
export type OpenSettings = {
    readonly type: "open-settings";
    readonly section: SettingsSectionKind;
};
// Leave Settings the way it was entered — its own unguarded close intent, like
// every other singleton tab (regions: paneTree).
export type CloseSettings = {
    readonly type: "close-settings";
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};

// --- Home overview (regions: paneTree) --
// Open (or re-focus) the Home overview singleton TAB — the workspace-overview
// surface (dogfood F1). The rail's Home icon fires this; re-entering while
// already on Home only re-focuses the one tab, never duplicates.
type OpenHome = { readonly type: "open-home" };
// Leave Home the way it was entered — its own unguarded close intent, like
// every other singleton tab (regions: paneTree).
type CloseHome = {
    readonly type: "close-home";
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};

// --- Studio canvas (regions: paneTree) --
// Open (or re-focus) the Studio canvas singleton TAB (Studio v0): the sidebar's
// Studio row fires this; re-entering only re-focuses the one tab.
type OpenStudio = {
    readonly type: "open-studio";
    // Open the Studio on this project (absent: on the one the author is in).
    readonly projectId?: string;
};
// Leave the Studio — its own unguarded close intent, like Home.
type CloseStudio = {
    readonly type: "close-studio";
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};

// --- Projects list (regions: paneTree) --
// Open (or re-focus) the Projects list singleton TAB (#161): the sidebar's
// Projects row fires this.
type OpenProjects = { readonly type: "open-projects" };
// Leave the Projects list — its own unguarded close intent, like Home.
type CloseProjects = {
    readonly type: "close-projects";
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};

// --- Knowledge bank (regions: paneTree) --
// Open (or re-focus) the Knowledge bank singleton TAB (#60) at exactly this
// place: a source's viewer (`selectedId`, `target` where in it to open) or,
// with no `selectedId`, the list. Every source-open lands here (sources live
// in the Knowledge bank, not the Library: #59).
type OpenKnowledge = {
    readonly type: "open-knowledge";
    readonly selectedId?: ResourceId;
    readonly target?: FocusTarget;
};
// Leave the Knowledge bank — its own unguarded close intent, like Home.
type CloseKnowledge = {
    readonly type: "close-knowledge";
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};

// --- Search + Recent (regions: paneTree) --
// Open (or re-focus) the Search + Recent/browse singleton TAB (dogfood F6) —
// the full-page successor to the retired `{type:"search"}` overlay. The rail's
// Search + Recent doors fire this; re-entering only re-focuses the one tab.
// `kind` optionally pre-selects a facet (chrome-shell rail categories/F33's
// "open search filtered by kind" reuse) — see ActiveSearch. `selectedId`
// optionally pre-selects a row (F88): a source-open now routes here with
// {kind:"source", selectedId} so the source lands SELECTED in the library
// detail (its F84 home) instead of the retired full-page source flik.
type OpenSearch = {
    readonly type: "open-search";
    readonly kind?: SearchFacet;
    readonly selectedId?: ResourceId;
    // Where in the selected source to open (e.g. a page a chat tool read).
    readonly target?: FocusTarget;
    // The library's query text (a `/library?q=` URL); absent ⇒ seed from kind.
    readonly query?: string;
};
// The author went INTO a library row (drill-in), stepped to its neighbour, or
// came back out to the list: the opened row is recorded on the active Search
// tab so the URL can mirror it (B4, `/library/{id}`). Omitted `selectedId`
// returns to the list. `target` is where in it to open (a content hit's page).
type SelectLibraryRow = {
    readonly type: "select-library-row";
    readonly selectedId?: ResourceId;
    readonly target?: FocusTarget;
};
// The library's query text changed (typed, a kind tab, a chip removed, an
// autocomplete pick): recorded on the active Search tab, mirrored to `?q=`.
type SetLibraryQuery = {
    readonly type: "set-library-query";
    readonly query: string;
};
// Leave Search the way it was entered — its own unguarded close intent, like
// every other singleton tab (regions: paneTree).
type CloseSearch = {
    readonly type: "close-search";
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};

// --- Preview lifecycle (regions: paneTree; + history via the blanket
// recorder — a glance is a focused view like any other) --
// A single-click GLANCE: open `ref` as the focused pane's ONE weak preview tab
// (VS Code's italic slot). Distinct from `open-resource` (which KEEPS), never a
// `preview: boolean` flag on it — the register is structural (nav-history §3a).
export type OpenResourcePreview = {
    readonly type: "open-resource-preview";
    readonly ref: ResourceRef;
};
// Double-click the glance → promote it to a KEPT tab. Its own intent (the
// recorder flips the history entry preview→kept in place, no new entry).
export type PromotePreviewTab = { readonly type: "promote-preview-tab" };
// Close the glance (× / middle-click on the preview tab). Never guarded (a
// glance is never dirty) and NOT remembered on the reopen stack.
export type ClosePreview = { readonly type: "close-preview" };
// An edit committed on a material promotes it if it is the current glance
// (edit-must-keep). A no-op when the edited id is not the preview.
export type ResourceEdited = {
    readonly type: "resource-edited";
    readonly id: ResourceId;
};

// --- Focus history (regions: paneTree, chat, history; + the arms
// materializeView routes) --
// Walk the window's ONE focus-history stack. Each direction its own intent
// (distinct-commands, never a `direction` flag). `nav-back` re-materializes the
// previous view — resurrecting one that left the working set as a glance or a
// kept tab (D1); past the first entry it is a no-op (D13). `nav-forward` is the
// symmetric return. Neither records focus (the cursor moves, it does not push).
export type NavBack = { readonly type: "nav-back" };
export type NavForward = { readonly type: "nav-forward" };

// --- New tab (regions: paneTree, chat) --
// Open a fresh NASCENT conversation tab in the focused pane — the NEW-TAB intent
// (⌘T / the strip "+"). MULTIPLE nascent conversations are allowed (no dedup):
// every dispatch appends a new one and activates it. The `conversationId` is
// SHELL-MINTED (a UUID, §3.5 — ids never in the pure fold) so the tab carries a
// durable-ready id from the moment it opens. Exits a full chat per D8.
export type NewTab = {
    readonly type: "new-tab";
    readonly conversationId: ConversationId;
};

// --- Full-quality report tab (regions: paneTree, chat) --
// Open (or re-focus) the full-quality report for a material as its OWN tab
// (owner decision: "full quality borde vara en egen flik"). Distinct from the
// dock's `summon-quality` (which toggles the resting QualitySummary panel) — a
// separate intent, never a flag on it. The `title` is the material's display
// title for the tab label; the evaluation is loaded data the shell sources by
// `materialId` (never carried here — the frame holds no loaded data). Exits a
// full chat per D8.
export type OpenQualityReport = {
    readonly type: "open-quality-report";
    readonly materialId: ResourceId;
    readonly title: string;
};
// Close a material's full-quality report tab — its own unguarded intent (a
// report holds nothing unsaved), focus falling to the neighbour.
export type CloseQualityReport = {
    readonly type: "close-quality-report";
    readonly materialId: ResourceId;
    // Shell-minted seed for the never-zero-tabs case (see CloseConversationTab).
    readonly freshConversationId: ConversationId;
};

// --- Panes / split views (regions: paneTree) --
// Split the FOCUSED pane, placing its ACTIVE tab in a new pane in `direction`
// (split-views §6). `direction` is a genuine 4-value ARGUMENT (where the new
// pane goes), not a meaning-swapping flag. The new leaf + wrapping branch ids
// are SHELL-MINTED (ids never in the pure fold) and carried on the command.
export type SplitDirection = "left" | "right" | "up" | "down";
export type SplitFocusedPane = {
    readonly type: "split-focused-pane";
    readonly direction: SplitDirection;
    readonly newLeafId: PaneId;
    readonly newBranchId: PaneId;
};
// Distinct next/prev focus intents (mirroring NextTab/PreviousTab — not one
// cycle{dir} flag).
export type FocusPaneNext = { readonly type: "focus-pane-next" };
export type FocusPanePrevious = { readonly type: "focus-pane-previous" };
// Activate a tab in a SPECIFIC pane (a click in a non-focused pane's strip):
// focuses that pane AND activates the tab, so the two are one gesture.
export type ActivateInPane = {
    readonly type: "activate-in-pane";
    readonly pane: PaneId;
    readonly tab: ActiveTab;
};
// Close the focused pane; its tabs merge into a sibling, the split collapses.
export type ClosePaneT = { readonly type: "close-pane" };
// Resize: shift mass between child i and i+1 of `branch`. `delta` is a
// proportion (the shell converted px→fraction at pointer-up).
export type ResizeDividerT = {
    readonly type: "resize-divider";
    readonly branch: PaneId;
    readonly dividerIndex: number;
    readonly delta: number;
};

// --- Messages (regions: messages) --
// A new notice, stamped with when it happened (ms; the shell reads the clock).
// Where it shows is decided in the fold (messages.ts).
export type ShowNotice = {
    readonly type: "show-notice";
    readonly notice: HostNotice;
    readonly at: number;
};
// The stack's own inputs: the clock, hover/focus, close, "+N more".
type ControlMessages = {
    readonly type: "control-messages";
    readonly control: QueueControl;
};

export type FrameTransition =
    | CollapseRail
    | ExpandRail
    | HideRail
    | ToggleRail
    | OpenOverlay
    | CloseOverlay
    | SummonSlot
    | CloseSecondary
    | SummonReview
    | SummonQuality
    | SummonPanel
    | FocusOverview
    | FocusEntity
    | ShowInPanel
    | ShowStatementSource
    | EnterFullChat
    | CreateMaterialFromSource
    | CloseConversationTab
    | PinChat
    | UnpinChat
    | OpenResource
    | OpenResourcePreview
    | PromotePreviewTab
    | ClosePreview
    | ResourceEdited
    | NavBack
    | NavForward
    | ActivateTab
    | CloseTab
    | CloseActiveTab
    | CloseOtherTabs
    | CloseTabsToRight
    | KeepUnsavedTab
    | DiscardUnsavedTab
    | ReopenClosedTab
    | DropResourceTab
    | RetitleResource
    | ReconcileTabs
    | OpenSettings
    | CloseSettings
    | OpenHome
    | CloseHome
    | OpenStudio
    | CloseStudio
    | OpenProjects
    | CloseProjects
    | OpenKnowledge
    | CloseKnowledge
    | OpenSearch
    | SelectLibraryRow
    | SetLibraryQuery
    | CloseSearch
    | NewTab
    | OpenQualityReport
    | CloseQualityReport
    | SplitFocusedPane
    | FocusPaneNext
    | FocusPanePrevious
    | ActivateInPane
    | ClosePaneT
    | ResizeDividerT
    | ShowNotice
    | ControlMessages;
